// src/support/salesforceBatch.ts
//
// Pure helpers for batching Salesforce reads through the raw SOQL passthrough (`sf/query`) instead of
// fanning out one scalar detail request per id. The structured list filters silently drop an `Id`
// anyOf clause ([[salesforce-id-list-filters-are-dropped]]), but a raw `WHERE Id IN (...)` sent through
// `sf/query` bypasses that translator entirely and is fully supported — this is how the rest of the app
// already queries Salesforce. The one responsibility that moves to the caller is escaping: `sf/query`
// does not sanitize, so every id MUST be validated before it is interpolated into the IN list.

/** A valid 15- or 18-char Salesforce id (alphanumeric only). Anything else is dropped, which is what
 *  makes the interpolated `IN` list injection-safe: a `[0-9a-zA-Z]{15,18}` token cannot escape a quoted
 *  literal. Mirrors the sanitizer at components/LandingPage/LandingPageV2.tsx and the Go client's
 *  ValidOpportunityID guard. */
const SF_ID = /^[a-zA-Z0-9]{15,18}$/;

/** The per-request id budget. The binding constraint is the `?q=` GET URL length (an 18-char quoted id
 *  + separator ≈ 23 chars, so 200 ids ≈ 4.6 KB — safely under a URL budget and far under SOQL's own
 *  IN-list cap). For the bounded volumes these batches see (dozens–low hundreds) this is usually one
 *  request. */
export const SF_ID_CHUNK = 200;

/** Filter to valid Salesforce ids and de-dupe, preserving first-seen order. */
export function sanitizeSfIds(ids: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids || []) {
    const id = String(raw ?? '').trim();
    if (SF_ID.test(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Split into chunks of at most `size` (size ≤ 0 → a single chunk). */
export function chunk<T>(arr: T[], size: number): T[][] {
  if (size <= 0) return arr.length ? [arr] : [];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** `SELECT <fields> FROM <object> WHERE Id IN ('a', 'b', ...)` for PRE-SANITIZED ids. Never pass raw
 *  ids here — run them through sanitizeSfIds first. */
export function soqlInById(object: string, fields: string[], ids: string[]): string {
  const inList = ids.map((id) => `'${id}'`).join(', ');
  return `SELECT ${fields.join(', ')} FROM ${object} WHERE Id IN (${inList})`;
}

/** Look up a batched record by a 15- or 18-char id, tolerating the 15↔18 form mismatch. Salesforce
 *  always returns the 18-char `Id`; a caller holding the 15-char form (or vice versa) still resolves
 *  because the map is keyed by both the full id and its case-sensitive 15-char prefix. */
export function getSfRecord<T = any>(byId: Map<string, T>, id: string | null | undefined): T | undefined {
  if (!id) return undefined;
  const direct = byId.get(id);
  if (direct !== undefined) return direct;
  return id.length >= 15 ? byId.get(id.slice(0, 15)) : undefined;
}
